/**
 * Viewport-fill helpers for CTX Reflector V2 list tables (CTX-331 / CTX-244).
 *
 * `GenericTable` falls back to a fixed 400px body when `maxTableBodyHeight` is
 * omitted, which strands most of a tall viewport. These helpers are the one
 * source of truth for opting a Reflector V2 list into a `calc(100vh - Npx)`
 * budget and a first page that actually fills that height.
 *
 * Do not change GenericTable's 400px default — Kontrol Edge / Insight still
 * depend on it. Each V2 list opts in by passing these values.
 */

/** Ascending — `getInitialRowsPerPage` relies on the order to pick the smallest fit. */
export const REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS = [10, 25, 50] as const;

export type ReflectorV2RowsPerPageOption =
  (typeof REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS)[number];

/**
 * CSS max-height for the table body: remaining viewport after page chrome
 * (SideNav, tabs, headers, card padding, pagination footer).
 */
export function getTableBodyMaxHeight(chromePx: number): string {
  return `calc(100vh - ${chromePx}px)`;
}

/**
 * Smallest option that still shows every row on one page, falling back to the
 * largest option when the list is longer than any option covers.
 *
 * `GenericTable` reads `initialRowsPerPage` once at mount, so the caller must
 * only mount the table after the row count is known (e.g. behind DataStateView).
 */
export function getInitialRowsPerPage(
  rowCount: number,
  options: readonly number[] = REFLECTOR_V2_ROWS_PER_PAGE_OPTIONS
): number {
  if (options.length === 0) {
    return rowCount;
  }
  return options.find((option) => option >= rowCount) ?? options[options.length - 1];
}
